/**
 * Pricing — placeholder pricing page per product-copy spec §4.10.
 *
 * Three tiers without numbers (real prices set at signup). Each card links
 * to /demo. Comparison link below.
 *
 * Iteration 46: migrated to i18n via marketing.pricing_v2.* keys.
 */
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import MetaTags from "../components/MetaTags";
import { useI18n } from "../lib/i18n";

const TIER_KEYS = ["essentials", "pro", "enterprise"];
const FEATURE_COUNT = 6;

export default function Pricing() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-screen">
      <MetaTags
        title={t("marketing.pricing_v2.meta_title")}
        description={t("marketing.pricing_v2.meta_description")}
      />
      <MarketingNav />
      <main className="pt-32 sm:pt-40 pb-24">
        <section className="mx-auto max-w-7xl px-6 sm:px-10 text-center">
          <span className="eyebrow">{t("marketing.pricing_v2.eyebrow")}</span>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            {t("marketing.pricing_v2.headline_lead")}
            <br />
            <span className="italic font-light text-[#1F3A2A]">
              {t("marketing.pricing_v2.headline_italic")}
            </span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-[#6B7280] leading-relaxed">
            {t("marketing.pricing_v2.subhead")}
          </p>
        </section>

        <section className="mx-auto max-w-7xl px-6 sm:px-10 mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TIER_KEYS.map((tierKey) => {
            const featured = tierKey === "pro";
            return (
              <article
                key={tierKey}
                data-testid={`pricing-${tierKey}`}
                className={
                  "rounded-3xl p-8 border transition-all duration-200 " +
                  (featured
                    ? "bg-[#132619] text-white border-[#1A1B2E] shadow-[0_16px_40px_rgba(26,27,46,0.18)] -translate-y-2"
                    : "bg-white border-[#EEF0F4] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)]")
                }
              >
                <div
                  className={
                    "text-xs tracking-[0.04em] uppercase font-semibold " +
                    (featured ? "text-[#3D5C46]" : "text-[#6B7280]")
                  }
                >
                  {t(`marketing.pricing_v2.tier_${tierKey}_name`)}
                </div>
                <h2
                  className={
                    "mt-3 font-display text-3xl tracking-tight leading-tight " +
                    (featured ? "text-white" : "text-[#1A1B2E]")
                  }
                >
                  {t(`marketing.pricing_v2.tier_${tierKey}_tagline`)}
                </h2>
                <p
                  className={
                    "mt-3 text-sm " +
                    (featured ? "text-white/70" : "text-[#6B7280]")
                  }
                >
                  {t(`marketing.pricing_v2.tier_${tierKey}_audience`)}
                </p>

                <Link
                  to="/demo"
                  data-testid={`pricing-${tierKey}-cta`}
                  className={
                    "mt-8 inline-flex w-full items-center justify-center px-6 py-3 rounded-full font-semibold text-sm transition-colors " +
                    (featured
                      ? "bg-white text-[#1A1B2E] hover:bg-[#E6EFE8]"
                      : "bg-[#1F3A2A] text-white hover:bg-[#132C17]")
                  }
                >
                  {t("marketing.pricing_v2.cta_talk")}
                </Link>

                <ul className="mt-8 space-y-3">
                  {Array.from({ length: FEATURE_COUNT }).map((_, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check
                        size={16}
                        strokeWidth={2}
                        className={
                          "mt-0.5 shrink-0 " +
                          (featured ? "text-[#3D5C46]" : "text-[#1F3A2A]")
                        }
                      />
                      <span
                        className={
                          "text-sm leading-relaxed " +
                          (featured ? "text-white/85" : "text-[#1A1B2E]")
                        }
                      >
                        {t(`marketing.pricing_v2.tier_${tierKey}_feature_${i + 1}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                <p
                  className={
                    "mt-6 text-xs " +
                    (featured ? "text-white/50" : "text-[#9CA3AF]")
                  }
                >
                  {t("marketing.pricing_v2.no_surprises")}
                </p>
              </article>
            );
          })}
        </section>

        <section className="mx-auto max-w-7xl px-6 sm:px-10 mt-14 text-center">
          <Link
            to="/demo"
            data-testid="pricing-compare"
            className="text-sm font-medium text-[#1F3A2A] underline underline-offset-4"
          >
            {t("marketing.pricing_v2.compare_link")}
          </Link>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
